import { useResponse } from "contexts/response";
import { useCallback } from "react";

import { TruckingCompany } from "types";

const BASE_URL = new URL('/api/trucking-companies', window.location.origin);

export const useCarrierAPI = () => {
  const { handleError, setSuccess } = useResponse();

  const resolveErrorStatus = useCallback(
    (response: Response, json: any) => {
      switch (response.status) {
        case 500:
          handleError('INTERNAL SERVER ERROR');
          return null;
        case 403:
          handleError('FORBIDDEN');
          return null;
        case 401:
          handleError('UNAUTHORIZED');
          return null;
        default:
          handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
          return null;
      }
    }, [handleError]
  );

  const getAllCarriers = useCallback(
    async (includeDeactivated: boolean): Promise<TruckingCompany[] | null> => {
      const options: RequestInit = {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      try {
        const response = await fetch(`${BASE_URL}?includeDeactivated=${includeDeactivated}`, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok) {
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus]
  );

  const activateCarrier = useCallback(
    async (carrierId: number): Promise<TruckingCompany | null> => {
      const options: RequestInit = {
        method: 'PUT',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      try {
        const response = await fetch(`${BASE_URL}/activate/${carrierId}`, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess('Carrier activated.');
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    }, [handleError, resolveErrorStatus, setSuccess]
  );

  const deactivateCarrier = useCallback(
    async (carrierId: number): Promise<TruckingCompany | null> => {
      const options: RequestInit = {
        method: 'PUT',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      try {
        const response = await fetch(`${BASE_URL}/deactivate/${carrierId}`, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess('Carrier deactivated.');
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    }, [handleError, resolveErrorStatus, setSuccess]
  );

  const createCarrier = useCallback(
    async (carrier: Partial<TruckingCompany>): Promise<TruckingCompany | null> => {
      const options: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(carrier)
      }

      try {
        const response = await fetch(BASE_URL, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess('Carrier Created!');
          return json;
        }

        return resolveErrorStatus(response, json)
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus, setSuccess]
  );

  const updateCarrier = useCallback(
    async (carrier: TruckingCompany): Promise<TruckingCompany | null> => {
      const options: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(carrier)
      }

      try {
        const response = await fetch(BASE_URL, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess('Carrier Updated!');
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus, setSuccess]
  );

  return { getAllCarriers, activateCarrier, deactivateCarrier, createCarrier, updateCarrier };
}